import type { AuctisyncSupabase } from './client.js';
import { fetchLotsByIds, type PublicLotRow } from './lots.js';

/**
 * The bidder's watchlist — bookmarked lots (Run Brief P2.7).
 *
 * WHY THIS LIVES HERE. The website already implements all of this twice: the
 * toggle inside `apps/web/src/routes/pages/PublicLotDetail.tsx` and the read
 * inside `BidderDashboard.tsx`. The phone needs the same rules, and "share
 * LOGIC only" (Run Brief D-share) means extracting them rather than writing a
 * third copy — Jim's standing rule is that there is no mechanical divergence
 * between the app and the website.
 *
 * WATCHING IS NOT BIDDING. Nothing in this file touches money, the `bids`
 * ledger or `place_bid`. A watch is a private bookmark: `bidder_watchlist` is
 * `(user_id, lot_id)` with RLS scoped to `auth.uid()` for select, insert and
 * delete, so a bidder can only ever see and change their own. The table is not
 * readable by `anon` at all, which is why every function here needs a signed-in
 * caller and returns nothing useful without one.
 *
 * `lots.watch_count` is a separate, aggregate, PUBLIC number maintained
 * server-side. It is deliberately not written from here — a client that could
 * increment a counter directly could inflate it, and the interest signal a
 * bidder sees on a lot would stop meaning anything.
 */

/**
 * Add a lot to the caller's watchlist. Idempotent.
 *
 * `upsert(..., ignoreDuplicates)` rather than a plain insert, because the
 * primary key is `(user_id, lot_id)` and a second insert raises 23505. The
 * website treats any error as "did not work" and leaves the heart un-filled, so
 * watching a lot you ALREADY watch currently reports failure and the control
 * reads as off while the row is right there in the table. Two devices, or one
 * impatient double-tap, is all it takes. Making the operation idempotent means
 * "watch this" always ends in the same state: watched.
 *
 * `userId` is passed rather than looked up so a caller that already holds the
 * session does not pay for a round trip. It is not a security control — the RLS
 * `with check (user_id = auth.uid())` refuses somebody else's id regardless.
 */
export async function addToWatchlist(
  supabase: AuctisyncSupabase,
  userId: string,
  lotId: string,
): Promise<{ error?: string }> {
  const { error } = await supabase
    .from('bidder_watchlist')
    .upsert(
      { user_id: userId, lot_id: lotId },
      { onConflict: 'user_id,lot_id', ignoreDuplicates: true },
    );
  return error ? { error: error.message } : {};
}

/**
 * Remove a lot from the caller's watchlist. Idempotent by nature — deleting a
 * row that is not there is a successful no-op, not an error.
 */
export async function removeFromWatchlist(
  supabase: AuctisyncSupabase,
  userId: string,
  lotId: string,
): Promise<{ error?: string }> {
  const { error } = await supabase
    .from('bidder_watchlist')
    .delete()
    .eq('user_id', userId)
    .eq('lot_id', lotId);
  return error ? { error: error.message } : {};
}

/**
 * Set the watched state directly.
 *
 * Preferred over a caller-side `if (watched) remove() else add()` because BOTH
 * halves are idempotent: this converges on the state asked for even if the
 * caller's idea of the current state was stale. A toggle derived from stale
 * state does the opposite of what the bidder pressed.
 */
export async function setWatched(
  supabase: AuctisyncSupabase,
  userId: string,
  lotId: string,
  watched: boolean,
): Promise<{ error?: string }> {
  return watched
    ? addToWatchlist(supabase, userId, lotId)
    : removeFromWatchlist(supabase, userId, lotId);
}

/**
 * Whether the caller is watching one specific lot.
 *
 * `maybeSingle` so "not watching" comes back as `null` rather than as an error
 * about zero rows. A failed lookup answers `false`: a lot shown as un-watched
 * that is actually watched costs one redundant tap, and the idempotent add
 * above makes that tap harmless.
 */
export async function isLotWatched(
  supabase: AuctisyncSupabase,
  userId: string,
  lotId: string,
): Promise<boolean> {
  const { data } = await supabase
    .from('bidder_watchlist')
    .select('lot_id')
    .eq('user_id', userId)
    .eq('lot_id', lotId)
    .maybeSingle();
  return data !== null && data !== undefined;
}

/**
 * Every lot id the caller is watching, newest bookmark first.
 *
 * Ids rather than lots, so a surface that only needs to know "is this one
 * watched" (a feed marking its tiles) does not pay to read every lot row.
 */
export async function fetchMyWatchedLotIds(
  supabase: AuctisyncSupabase,
  userId: string,
): Promise<string[]> {
  const { data, error } = await supabase
    .from('bidder_watchlist')
    .select('lot_id')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((row) => row.lot_id);
}

/**
 * The watchlist as lots, in the order they were bookmarked (newest first).
 *
 * Two reads on purpose. The membership rows carry no lot detail, and reading
 * the lots through `fetchLotsByIds` keeps ONE public column list in the
 * codebase — an embedded `lots(...)` join would be a second place to keep in
 * step with the anon column grants, and the grants are the security boundary.
 *
 * `.in()` returns rows in the database's order, not the order of the ids, so
 * the bookmark order is re-imposed here. Without that the list would reshuffle
 * itself between visits for no reason a bidder could see.
 *
 * A watched lot that is no longer publicly readable (withdrawn, or its auction
 * unpublished) simply does not appear. That matches what the website's
 * dashboard already does with the same row, and `missing` reports how many fell
 * out so a caller can say so rather than quietly showing a shorter list.
 */
/**
 * Lot statuses that still take bids. Everything else is a finished lot.
 *
 * Declared here rather than inlined so the two sections of the watchlist and any
 * later caller cannot disagree about what "open" means.
 */
const OPEN_LOT_STATUSES = ['scheduled', 'active', 'closing'] as const;

/**
 * Is this lot still biddable?
 *
 * Exported because the watchlist is SECTIONED by it on both surfaces, and both
 * must derive the sections from the lots they are currently showing rather than
 * from a snapshot taken at fetch time — the phone re-renders watched lots from a
 * realtime channel, so a lot that closes while the screen is open has to move
 * from Open to Closed by itself.
 */
export function isLotStillOpen(lot: Pick<PublicLotRow, 'status'>): boolean {
  return (OPEN_LOT_STATUSES as readonly string[]).includes(lot.status);
}

/** The auction status the platform uses for "this sale is over". */
const FINISHED_AUCTION_STATUS = 'completed';

export interface WatchlistResult {
  /**
   * Everything still on the watchlist — `open` and `closed` together, in the
   * bidder's own newest-first order. Lots whose SALE has finished are not here;
   * see the note on `closed`.
   */
  lots: PublicLotRow[];
  /** Still biddable, as it stood at fetch time. See the warning on `closed`. */
  open: PublicLotRow[];
  /**
   * Finished lots whose SALE is still running — the `closed` half of the
   * watchlist, as it stood at fetch time.
   *
   * ⚠️ SECTION THE UI WITH `isLotStillOpen`, NOT WITH THIS. A lot can close
   * while the screen is open, and the phone re-renders from a realtime channel;
   * a section built from this array would strand it in the wrong half. This is
   * here for callers that only need the counts.
   *
   * 🔴 A LOT LEAVES THE WATCHLIST WHEN ITS SALE ENDS, NOT WHEN IT CLOSES. Jim,
   * 2026-09-08. While the sale is on, a bidder wants to see what happened to the
   * lots they watched — they lost one, so what is left? Once the whole sale is
   * over there is nothing to act on, and a watchlist that keeps filling up with
   * finished sales stops being a working list.
   *
   * NOTHING IS DELETED. The `bidder_watchlist` row survives; this reader simply
   * stops returning it. A destructive sweep would be irreversible, would race
   * with a sale closing mid-read, and would throw away a record of what a bidder
   * was interested in — for a screen that only ever needed a filter.
   */
  closed: PublicLotRow[];
  /** Watched entries whose lot is no longer publicly readable. */
  missing: number;
  /** Watched lots hidden because their sale has finished. */
  settled: number;
}

export async function fetchMyWatchlist(
  supabase: AuctisyncSupabase,
  userId: string,
): Promise<WatchlistResult> {
  const ids = await fetchMyWatchedLotIds(supabase, userId);
  // No empty-list guard here on purpose: `fetchLotsByIds` owns that check, and a
  // second copy made BOTH untestable — removing either one alone left the other
  // covering for it, so neither could be shown to do anything.
  const rows = await fetchLotsByIds(supabase, ids);
  const byId = new Map(rows.map((row) => [row.id, row]));
  const found = ids
    .map((id) => byId.get(id))
    .filter((row): row is PublicLotRow => row !== undefined);

  // Which of these sales have finished? One read for the whole list, keyed on
  // the auctions actually represented — not one per lot.
  const auctionIds = [...new Set(found.map((l) => l.auction_id))];
  const finished = new Set<string>();
  if (auctionIds.length > 0) {
    const { data } = await supabase.from('auctions').select('id, status').in('id', auctionIds);
    for (const row of (data ?? []) as { id: string; status: string }[]) {
      if (row.status === FINISHED_AUCTION_STATUS) finished.add(row.id);
    }
  }

  const live = found.filter((l) => !finished.has(l.auction_id));

  return {
    lots: live,
    open: live.filter(isLotStillOpen),
    closed: live.filter((l) => !isLotStillOpen(l)),
    missing: ids.length - found.length,
    settled: found.length - live.length,
  };
}
